import { useQueryClient } from '@tanstack/react-query';

import {
  getActivePlanAssignmentsQueryKey,
  getManagedPlansQueryKey,
  getPlanReportQueryKey,
  getPlansSummaryQueryKey,
  getUserSummaryReportQueryKey,
} from '@cfa/pathway-fe-shared/xp';

/**
 * Provides a function to invalidate all certificate-related queries.
 *
 * Call this after plan completion or reassignment to ensure PrintCertificates
 * components show updated counts and data.
 *
 * @example
 * const invalidateCertificates = useInvalidateCertificateQueries();
 *
 * await someMutation();
 * invalidateCertificates({ planId, userId });
 */
export const useInvalidateCertificateQueries = () => {
  const queryClient = useQueryClient();

  return async (options?: { planId?: string; userId?: string }) => {
    const invalidations: Promise<void>[] = [];

    // List caches used for certificate counts
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: getPlansSummaryQueryKey(),
        refetchType: 'all',
      }),
    );

    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: getManagedPlansQueryKey(),
        refetchType: 'all',
      }),
    );

    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: getActivePlanAssignmentsQueryKey(),
        refetchType: 'all',
      }),
    );

    if (options?.planId) {
      invalidations.push(
        queryClient.invalidateQueries({
          queryKey: getPlanReportQueryKey({ path: { planId: options.planId } }),
          refetchType: 'all',
        }),
      );
    }

    // Invalidate specific user summary if userId provided
    if (options?.userId) {
      invalidations.push(
        queryClient.invalidateQueries({
          queryKey: getUserSummaryReportQueryKey({
            path: { userId: options.userId },
          }),
          refetchType: 'all',
        }),
      );
    }

    await Promise.all(invalidations);
  };
};
